var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import copy from "fast-copy";
import { useAppDispatch, useAppSelector } from "@/hooks/store";
import { ThemeVariant } from "./types";
import { useLogger } from "@/utils/loggerService";
import themePresets from "@/utils/themes/presets";
import { updateTheme } from "@/store/themeSlice";
import { useMemo } from "react";
import { useGetCustomizeAdminThemesQuery } from "@/store/api";
import { useParams } from "react-router-dom";
import { numberSlotLimit } from "@/components/forms/components/utils";
import { THEME_RUNTIME_CONFIG_COLOR_DARK, THEME_RUNTIME_CONFIG_COLOR_LIGHT, createVariableByTheme, } from "../funcs";
export function useTheme() {
    var _a, _b, _c;
    var logger = useLogger().logger;
    var defaultTheme = (function () {
        var themes = Object.values(themePresets);
        var defaultTheme = themes.filter(function (t) { return t.default === true; });
        return defaultTheme[0] || copy(themes[0]);
    })();
    var defaultVariant = (function () {
        var variants = Object.keys(defaultTheme.variants);
        return variants[0] || ThemeVariant.LIGHT;
    })();
    var dispatch = useAppDispatch();
    var themeVariantName = useAppSelector(function (state) { return state.theme.variant; });
    var theme = useAppSelector(function (state) { return state.theme.theme; });
    var baseVariables = useAppSelector(function (state) { return state.theme.variables; });
    var _d = useAppSelector(function (state) { return state.theme; }), colorBase = _d.colorBase, fontOpacity = _d.fontOpacity, underlineColor = _d.underlineColor;
    var variableValue = themeVariantName === "dark"
        ? THEME_RUNTIME_CONFIG_COLOR_DARK
        : THEME_RUNTIME_CONFIG_COLOR_LIGHT;
    var configTheme = useGetCustomizeAdminThemesQuery(undefined).data;
    var colorsState = useAppSelector(function (state) { return state.colorsIcon.colors_state_list; });
    var fontOpacityValue = fontOpacity || ((_a = configTheme === null || configTheme === void 0 ? void 0 : configTheme.data) === null || _a === void 0 ? void 0 : _a.font_opacity_settings);
    var theme_color = colorBase || ((_b = configTheme === null || configTheme === void 0 ? void 0 : configTheme.data) === null || _b === void 0 ? void 0 : _b.theme_color);
    var underline_color = underlineColor || ((_c = configTheme === null || configTheme === void 0 ? void 0 : configTheme.data) === null || _c === void 0 ? void 0 : _c.underline_color);
    var paletteColors = colorsState || [];
    var colorData = useMemo(function () {
        if (!fontOpacity)
            return {};
        return createVariableByTheme(theme_color, underline_color, fontOpacityValue, variableValue, paletteColors);
    }, [fontOpacityValue, theme_color, variableValue, paletteColors]);
    /**
     * Grouper et fusioner les variables dans le fichier theme dark/light
     *  ainsi les variables utilisent les configs
     **/
    var updateVariables = function (baseVariables, overrideVariables) {
        var updatedVariable = __assign({}, baseVariables);
        Object.entries(overrideVariables).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            if (updatedVariable[key] !== value) {
                updatedVariable[key] = value;
            }
        });
        return updatedVariable;
    };
    var variables = useMemo(function () {
        return updateVariables(baseVariables, colorData);
    }, [baseVariables, colorData]);
    var storedThemeVariant = useMemo(function () { return localStorage.getItem("themeVariant"); }, []);
    var currentThemeVariant = themeVariantName || storedThemeVariant || defaultVariant;
    var darkMode = currentThemeVariant === ThemeVariant.DARK;
    if (!theme || !themeVariantName) {
        var themeToUse = theme || defaultTheme; // Ensure theme object is set
        var variantToUse = currentThemeVariant || defaultVariant;
        logger.debug("[Theme] - Initializing theme with variant ".concat(variantToUse, " and theme object ").concat(themeToUse.label));
        dispatch(updateTheme({ theme: themeToUse, variant: variantToUse }));
    }
    if (themeVariantName && themeVariantName !== storedThemeVariant) {
        // localStorage.setItem("themeVariant", themeVariantName);
    }
    return {
        darkMode: darkMode,
        variables: variables,
    };
}
export var useColorIcons = function () {
    var colors = useAppSelector(function (state) { return state.colorsIcon.colors_state_list; });
    var getColorIcon = function () {
        if (!colors)
            return {};
        var moduleNames = [
            "Home",
            "Accounts",
            "Contacts",
            "Opportunities",
            "AOS_Quotes",
            "Leads",
            "Documents",
            "Emails",
            "AOS_Invoices",
            "AOS_Contracts",
            "Cases",
            "Prospects",
            "ProspectLists",
            "AM_ProjectTemplates",
            "FP_events",
            "FP_Event_Locations",
            "AOS_Products",
            "AOS_Product_Categories",
            "AOS_PDF_Templates",
            "jjwg_Maps",
            "jjwg_Markers",
            "jjwg_Address_Cache",
            "AOR_Reports",
            "AOW_WorkFlow",
            "AOK_KnowledgeBase",
            "AOK_Knowledge_Base_Categories",
            "Surveys",
            "EmailTemplates",
            "Project",
            "Users",
            "Calendar",
            "Notes",
            "Tasks",
            "ProjectTask",
            "Calls",
            "Campaigns",
            "Meetings",
        ];
        return moduleNames.reduce(function (acc, moduleName, index) {
            var _a;
            acc[moduleName] = (_a = colors[index]) !== null && _a !== void 0 ? _a : colors[0];
            return acc;
        }, {});
    };
    return getColorIcon();
};
export function useFilteredWidgets(slot) {
    var widgets = useAppSelector(function (state) { return state.layout.slots[slot].widgets; });
    var _a = useParams(), module = _a.module, action = _a.action;
    var actionUrl = action ? action : "index";
    return useMemo(function () {
        var _a, _b, _c;
        var usedWidth = 0;
        var filtered = [];
        for (var _i = 0, widgets_1 = widgets; _i < widgets_1.length; _i++) {
            var widget = widgets_1[_i];
            var width = (_b = (_a = widget.payload) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 1;
            var isPinned = ((_c = widget === null || widget === void 0 ? void 0 : widget.meta) === null || _c === void 0 ? void 0 : _c.pinned) === true;
            var isCurrent = module === (widget === null || widget === void 0 ? void 0 : widget.module) && actionUrl === (widget === null || widget === void 0 ? void 0 : widget.actionUrl);
            var shouldShow = isPinned || isCurrent;
            if (!shouldShow)
                continue;
            if (usedWidth + width > numberSlotLimit)
                continue;
            filtered.push(widget);
            usedWidth += width;
        }
        return filtered;
    }, [widgets, module, actionUrl]);
}
